/**
 * Clubs, plans and who may do what — the screen behind the menu's Admin item.
 *
 * ## Why there is a screen at all
 *
 * `app/user/cli.py` argues there is deliberately no UI for account management,
 * and the reason it gives is specific: granting `admin` is granting the ability
 * to act as other accounts, and a screen that could do that is a screen someone
 * can be tricked into using. That argument is about **roles**, and this screen
 * writes none — the closest it comes is moving an account onto a plan.
 *
 * What it edits is entitlements, where getting one wrong means somebody sees a
 * feature they did not pay for rather than somebody reading another user's
 * journal. And clubs are the thing that will change weekly, by whoever is
 * selling them, who is not at a terminal. A capability model whose only editor
 * is a CLI is a capability model nobody adjusts.
 *
 * ## Checkboxes over a text box, because three domains are data
 *
 * `/admin/vocabulary` serves the grantable values: markets from the registry,
 * indicator groups from the chart catalog, universes from the symbol store. A
 * free-text capability field would accept `ind:Seasonalty` silently and produce
 * a club whose members quietly lack the one thing they were sold.
 *
 * ## A grant is written the moment it is ticked
 *
 * No Save button. Every write is one row and idempotent, the version counter
 * makes it visible immediately, and the alternative — a dirty buffer over three
 * subject kinds — is where an admin loses a change by closing the panel. The
 * tick reverts if the server refuses, so the box always shows what is stored.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { plainText } from "@qufolio/screener-grid";
import { fetchGroups } from "../api/symbols";
import {
  bucketsFor,
  capabilitiesUnder,
  marketTree,
  stateOf,
  type GrantNode,
} from "../lib/grant-tree";
import { VirtualGroupIncludes, virtualBadge } from "./VirtualGroupIncludes";
import { GroupSales } from "./GroupSales";

import {
  addMember,
  createOrg,
  fetchEffective,
  fetchGrants,
  fetchMembers,
  fetchOrgs,
  fetchPlans,
  fetchUsers,
  fetchVocabulary,
  patchOrg,
  removeMember,
  revokeGrant,
  setGrant,
  setUserPlan,
  type AdminUser,
  type Effective,
  type Grant,
  type Member,
  type Org,
  type PlanGrants,
  type SubjectKind,
  type Vocabulary,
  deleteLayout,
  deletePreset,
  fetchAdminLayouts,
  fetchAdminPresets,
  setLayoutActive,
  fetchAdminChartTemplates,
  setChartTemplateActive,
  deleteChartTemplate,
  type AdminChartTemplate,
  setPresetActive,
  type AdminLayout,
  type AdminPreset,
} from "../api/admin";
import { AdminAi } from "./AdminAi";
import { AdminLedger } from "./AdminLedger";
import { AdminUsage } from "./AdminUsage";
import { GroupRequestsAdmin } from "./GroupRequestsAdmin";

const EMPTY_VOCABULARY: Vocabulary = { domains: [], features: [], limits: [] };

/**
 * How many accounts the group tab pulls in to name its member rows.
 *
 * The server caps `limit` at 200, so a full page means "there may be more" —
 * which is why a member with no account beside it is only called out as *no
 * account yet* when the page came back short.
 */
const DIRECTORY_LIMIT = 200;

const PANEL: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 12,
  width: 720,
  maxWidth: "92vw",
  maxHeight: "72vh",
  overflowY: "auto",
  padding: 14,
  color: "var(--qf-text)",
  font: "13px system-ui, sans-serif",
};

const TABS: React.CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: 4,
  borderBottom: "1px solid var(--qf-border)",
  paddingBottom: 6,
};

const SECTION: React.CSSProperties = {
  fontSize: 11,
  textTransform: "uppercase",
  letterSpacing: 0.6,
  color: "var(--qf-text-muted)",
  margin: "6px 0 2px",
};

const ROW: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "4px 0",
};

/**
 * The expander. Rendered `hidden` rather than omitted on a leaf, so every
 * checkbox in a level lines up — a column that jogs left on the rows without
 * children reads as two lists rather than one tree.
 */
const TWISTY: React.CSSProperties = {
  width: 14,
  flex: "0 0 auto",
  padding: 0,
  border: 0,
  background: "none",
  color: "var(--qf-text-secondary)",
  font: "inherit",
  fontSize: 11,
  lineHeight: 1,
  cursor: "pointer",
};

/** One level of the tree: a guide line, and the indent that goes with it. */
const NEST: React.CSSProperties = {
  marginLeft: 7,
  paddingLeft: 12,
  borderLeft: "1px solid var(--qf-border)",
};

const INPUT: React.CSSProperties = {
  background: "var(--qf-surface-alt)",
  color: "var(--qf-text)",
  border: "1px solid var(--qf-border)",
  borderRadius: 4,
  padding: "4px 6px",
  font: "inherit",
  minWidth: 0,
};

const NOTE: React.CSSProperties = {
  color: "var(--qf-text-secondary)",
  fontSize: 12,
  lineHeight: 1.5,
};

const ERROR: React.CSSProperties = { ...NOTE, color: "var(--qf-negative, #d66)" };

function tabStyle(active: boolean): React.CSSProperties {
  return {
    background: active ? "var(--qf-selection)" : "transparent",
    color: active ? "var(--qf-text)" : "var(--qf-text-secondary)",
    border: "1px solid",
    borderColor: active ? "var(--qf-border)" : "transparent",
    borderRadius: 4,
    padding: "4px 10px",
    font: "inherit",
    cursor: "pointer",
  };
}

function buttonStyle(): React.CSSProperties {
  return {
    background: "var(--qf-surface-alt)",
    color: "var(--qf-text)",
    border: "1px solid var(--qf-border)",
    borderRadius: 4,
    padding: "4px 10px",
    font: "inherit",
    cursor: "pointer",
  };
}

type Tab = "groups" | "plans" | "accounts" | "usage" | "presets" | "requests" | "ai" | "ledger";

/**
 * The grant editor: one checkbox per grantable capability, for one subject.
 *
 * Reused for a plan, a club and a user, because the three differ only in what
 * `subject` says — which is the same reason the server keeps them in one table.
 */
/**
 * One row of the grant tree, and its children.
 *
 * Rendered by hand rather than through a grid because the indentation is
 * structural: a guide line down each level is what makes depth readable, and
 * padding alone reads as a flat list with ragged margins.
 *
 * **A parent's tick writes its children, one capability each.** There is no
 * bulk endpoint, so the panel sends one `PUT` per row and reloads once at the
 * end; a partial failure leaves a mixed bucket, which is the honest state and
 * the same one `GrantEditor` already reloads rather than reverts into.
 */
function TreeRow({
  node,
  depth,
  holds,
  locked,
  onSet,
}: {
  node: GrantNode;
  depth: number;
  holds: (capability: string) => boolean;
  /** Everything reads ticked and nothing may be changed — the subject holds `*`. */
  locked: boolean;
  onSet: (capabilities: string[], on: boolean) => void;
}) {
  const [open, setOpen] = useState(depth === 0);
  const state = locked ? "on" : stateOf(node, holds);
  const capabilities = node.wildcard && node.capability ? [node.capability] : capabilitiesUnder(node);
  const branch = node.children.length > 0;

  return (
    <div>
      <div style={ROW}>
        <button
          type="button"
          aria-label={open ? "Collapse" : "Expand"}
          onClick={() => setOpen((was) => !was)}
          style={{ ...TWISTY, visibility: branch ? "visible" : "hidden" }}
        >
          {open ? "▾" : "▸"}
        </button>
        <input
          type="checkbox"
          checked={state === "on"}
          // The third state the DOM has no attribute for: a parent part-way is
          // neither ticked nor clear, and rendering it clear would invite a
          // click that silently re-grants what is already held.
          ref={(box) => {
            if (box) box.indeterminate = state === "mixed";
          }}
          disabled={locked || capabilities.length === 0}
          onChange={(event) => onSet(capabilities, event.target.checked)}
        />
        <span style={{ flex: "1 1 auto" }}>
          {node.wildcard ? <em>{node.label}</em> : node.label}
        </span>
        {node.hint && <span style={{ ...NOTE, flex: "0 0 auto" }}>{node.hint}</span>}
        {node.capability && !branch && (
          <span style={{ ...NOTE, flex: "0 0 auto", opacity: 0.65 }}>{node.capability}</span>
        )}
      </div>
      {branch && open && (
        // **The indent comes from the nesting, not from a depth multiplier.**
        // One container per level, each with its own guide line, so depth is
        // visible without counting pixels and a level added later needs no
        // arithmetic. A multiplier applied to a row that is already inside its
        // parent's container indents twice and reads as a ragged list.
        <div style={NEST}>
          {node.children.map((child) => (
            <TreeRow
              key={child.id}
              node={child}
              depth={depth + 1}
              holds={holds}
              locked={locked}
              onSet={onSet}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function GrantEditor({
  kind,
  subjectKey,
  vocabulary,
  onError,
}: {
  kind: SubjectKind;
  subjectKey: string;
  vocabulary: Vocabulary;
  onError: (message: string | null) => void;
}) {
  const [grants, setGrants] = useState<Grant[] | null>(null);

  /**
   * The symbol-group catalog, for the tree's middle level.
   *
   * `/admin/vocabulary` serves a code and a label and no `category`, so the
   * grouping comes from the catalog the picker already uses — which also
   * carries `groupType`, and that is what lets the journal's own five buckets
   * be dropped. Fetched once here rather than served from the vocabulary
   * because it needs no server change; moving it there is the tidier end state.
   */
  const [catalog, setCatalog] = useState<
    { code: string; market: string | null; category: string | null; groupType?: string; memberCount?: number }[]
  >([]);

  const reload = useCallback(() => {
    void fetchGrants(kind, subjectKey).then(setGrants);
  }, [kind, subjectKey]);

  useEffect(reload, [reload]);

  useEffect(() => {
    // Absence is survivable: `marketTree` drops to two levels rather than
    // failing, so a catalog that cannot be reached costs the grouping and not
    // the screen.
    void fetchGroups().then((found) => setCatalog(found ?? []));
  }, []);

  if (grants === null) return <div style={NOTE}>Loading…</div>;

  const held = new Map(grants.map((g) => [g.capability, g]));
  const everything = held.has("*");

  const toggle = async (capability: string, on: boolean) => {
    onError(null);
    try {
      if (on) await setGrant(kind, subjectKey, capability);
      else await revokeGrant(kind, subjectKey, capability);
      reload();
    } catch (error) {
      onError(error instanceof Error ? error.message : String(error));
      // Reloaded rather than reverted locally, so the box shows what is stored
      // even if the failure was partial.
      reload();
    }
  };

  /**
   * A whole branch at once — one request per capability, one reload at the end.
   *
   * There is no bulk grant endpoint, so a bucket of ten is ten `PUT`s and ten
   * version bumps. Sequential rather than parallel, because the server
   * invalidates on every write and a burst would have them queue anyway; and
   * the reload comes after the lot, so the boxes settle once rather than
   * flickering through the set.
   */
  const setMany = async (capabilities: string[], on: boolean) => {
    if (capabilities.length === 0) return;
    onError(null);
    try {
      for (const capability of capabilities) {
        if (on) await setGrant(kind, subjectKey, capability);
        else await revokeGrant(kind, subjectKey, capability);
      }
    } catch (error) {
      onError(error instanceof Error ? error.message : String(error));
    } finally {
      // Always, and after a failure especially: a half-applied bucket is the
      // honest state and the boxes must show it rather than what was intended.
      reload();
    }
  };

  const setLimit = async (capability: string, raw: string) => {
    onError(null);
    const value = raw.trim() === "" ? null : Number(raw);
    if (value !== null && !Number.isFinite(value)) return;
    try {
      await setGrant(kind, subjectKey, capability, { limit: value });
      reload();
    } catch (error) {
      onError(error instanceof Error ? error.message : String(error));
      reload();
    }
  };

  const box = (capability: string, label: string, hint?: string) => (
    <label key={capability} style={{ ...ROW, cursor: "pointer" }}>
      <input
        type="checkbox"
        checked={everything || held.has(capability)}
        onChange={(event) => void toggle(capability, event.target.checked)}
      />
      <span style={{ flex: "1 1 auto" }}>{label}</span>
      {hint && <span style={{ ...NOTE, flex: "0 0 auto" }}>{hint}</span>}
    </label>
  );

  return (
    <div>
      {everything && (
        <div style={NOTE}>
          Holds <code>*</code> — everything, in every domain. Untick it to grant
          capabilities one at a time; until then every box below is on because it
          genuinely is.{" "}
          <button
            type="button"
            style={buttonStyle()}
            onClick={() => void toggle("*", false)}
          >
            Remove <code>*</code>
          </button>
        </div>
      )}

      {vocabulary.domains.map((domain) => {
        /**
         * Two levels for indicators, three for markets — and in both the
         * domain wildcard keeps a row of its own above the tree.
         *
         * It is drawn apart rather than as the first branch because it is the
         * only row that says anything about the future: `matches()` answers yes
         * to any value in its domain, including one that does not exist yet.
         * Every row below it enumerates what exists today, and the two look
         * identical the moment they share a shape.
         */
        const nodes =
          domain.domain === "group"
            ? marketTree(domain.values, catalog)
            : bucketsFor(domain.values.map((value) => value.value));
        const wildcard = `${domain.domain}:*`;
        const wide = everything || held.has(wildcard);

        return (
          <div key={domain.domain}>
            <div style={SECTION}>{domain.label}</div>
            <label style={{ ...ROW, cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={wide}
                disabled={everything}
                onChange={(event) => void toggle(wildcard, event.target.checked)}
              />
              <span style={{ flex: "1 1 auto" }}>
                <strong>Everything</strong> — and anything added later
              </span>
              <span style={{ ...NOTE, flex: "0 0 auto" }}>{wildcard}</span>
            </label>
            {/* Hidden while the wildcard is held, because nothing below could
                subtract from it — the same rule the flat list followed, and the
                reason a "deny" on one of these rows would write a row that
                changes nothing. */}
            {!wide &&
              nodes.map((node) => (
                <TreeRow
                  key={node.id}
                  node={node}
                  depth={0}
                  holds={(capability) => held.has(capability)}
                  locked={everything}
                  onSet={(capabilities, on) => void setMany(capabilities, on)}
                />
              ))}
          </div>
        );
      })}

      <div style={SECTION}>Features</div>
      {vocabulary.features.map((feature) =>
        box(feature.capability, feature.label, feature.description),
      )}

      <div style={SECTION}>Daily budgets</div>
      <div style={NOTE}>
        Blank means no ceiling. The feature above decides whether the button
        exists at all; a budget only decides how often it may be pressed.
      </div>
      {vocabulary.limits.map((limit) => (
        <div key={limit.capability} style={ROW}>
          <span style={{ flex: "1 1 auto" }}>{limit.label}</span>
          <input
            type="number"
            min={0}
            defaultValue={held.get(limit.capability)?.limit ?? ""}
            placeholder="none"
            style={{ ...INPUT, width: 90 }}
            onBlur={(event) => void setLimit(limit.capability, event.target.value)}
          />
        </div>
      ))}
    </div>
  );
}

/**
 * Groups — the products QuFolio sells, and the clubs other people run.
 *
 * One list split in two rather than two tabs, because the *editing* is
 * identical: both hold members and both hold grants, and the grant editor does
 * not care which kind it is pointed at. What differs is the rule at the door —
 * a club refuses a member who holds no product — and that is the server's job,
 * not a second screen's.
 */
function Groups({
  vocabulary,
  onError,
}: {
  vocabulary: Vocabulary;
  onError: (message: string | null) => void;
}) {
  const [orgs, setOrgs] = useState<Org[] | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [members, setMembers] = useState<Member[]>([]);
  const [draft, setDraft] = useState({ key: "", name: "" });
  const [candidate, setCandidate] = useState("");
  const [directory, setDirectory] = useState<AdminUser[]>([]);
  const [matches, setMatches] = useState<AdminUser[]>([]);

  const reload = useCallback(() => {
    void fetchOrgs().then(setOrgs);
  }, []);
  useEffect(reload, [reload]);

  useEffect(() => {
    if (!selected) {
      setMembers([]);
      return;
    }
    void fetchMembers(selected).then(setMembers);
  }, [selected]);

  // A membership stores a key and nothing else, deliberately — it is the one
  // value that survives an email change and the only one that exists before the
  // account does. That makes the list unreadable on its own, so one page of the
  // directory is fetched to put an email against each row.
  useEffect(() => {
    void fetchUsers("", DIRECTORY_LIMIT).then(setDirectory);
  }, []);
  const byKey = useMemo(() => new Map(directory.map((each) => [each.key, each])), [directory]);

  // The picker asks the server rather than filtering `directory`, so it finds
  // an account past the first page. Debounced: this fires per keystroke.
  useEffect(() => {
    const text = candidate.trim();
    if (text.length < 2) {
      setMatches([]);
      return;
    }
    const timer = setTimeout(() => {
      void fetchUsers(text, 8).then(setMatches);
    }, 200);
    return () => clearTimeout(timer);
  }, [candidate]);

  const create = async () => {
    onError(null);
    if (!draft.key.trim() || !draft.name.trim()) {
      onError("A group needs a key and a name.");
      return;
    }
    try {
      await createOrg({ key: draft.key.trim(), name: draft.name.trim() });
      setDraft({ key: "", name: "" });
      reload();
    } catch (error) {
      onError(error instanceof Error ? error.message : String(error));
    }
  };

  /** Add one account — from a picked row, or from whatever was typed. */
  const addByKey = (orgKey: string, userKey: string) => {
    const key = userKey.trim();
    if (!key) return;
    onError(null);
    void addMember(orgKey, key)
      .then(() => {
        setCandidate("");
        setMatches([]);
        return fetchMembers(orgKey).then(setMembers);
      })
      .then(reload) // the member count on the group row
      .catch((error: unknown) =>
        onError(error instanceof Error ? error.message : String(error)),
      );
  };

  const org = orgs?.find((o) => o.key === selected) ?? null;

  return (
    <div>
      <div style={SECTION}>Groups</div>
      <div style={NOTE}>
        Access is groups and nothing else. A group holds grants; a user is in as
        many as you like, and what they can do is all of them added together.
      </div>
      {orgs === null && <div style={NOTE}>Loading…</div>}
      {orgs?.length === 0 && <div style={NOTE}>None yet.</div>}
      {orgs?.map((each) => (
        <div key={each.key} style={ROW}>
          <button
            type="button"
            style={{ ...buttonStyle(), flex: "1 1 auto", textAlign: "left" }}
            onClick={() => setSelected(each.key === selected ? null : each.key)}
          >
            {each.name}{" "}
            <span style={NOTE}>
              {each.key}
              {virtualBadge(each)
                ? ` · ${virtualBadge(each)}`
                : typeof each.memberCount === "number" && ` · ${each.memberCount} members`}
              {each.disabled && " · disabled"}
            </span>
          </button>
        </div>
      ))}

      <div style={SECTION}>New group</div>
      <div style={ROW}>
        <input
          style={{ ...INPUT, width: 130 }}
          placeholder="key"
          value={draft.key}
          onChange={(event) => setDraft({ ...draft, key: event.target.value })}
        />
        <input
          style={{ ...INPUT, flex: "1 1 auto" }}
          placeholder="name"
          value={draft.name}
          onChange={(event) => setDraft({ ...draft, name: event.target.value })}
        />
        <button type="button" style={buttonStyle()} onClick={() => void create()}>
          Create
        </button>
      </div>
      <div style={NOTE}>
        The key is what grants and memberships reference, and it can never be
        changed — renaming it would orphan both.
      </div>

      {org && (
        <>
          {/* A virtual group (mock 1): what it includes, set here, and the
              rule that keeps one member from receiving everything twice. */}
          <div style={SECTION}>
            {org.name}
            {virtualBadge(org) && <span style={NOTE}> {virtualBadge(org)}</span>}
          </div>
          <VirtualGroupIncludes org={org} orgs={orgs ?? []} onChanged={reload} onError={onError} />
          {/* What it sells, on this admin's own brand's Stripe account
              (`payments.md` § Q groups). Absent when billing is not set up. */}
          <GroupSales groupKey={org.key} onError={onError} />
          <div style={SECTION}>{org.name} — members</div>
          {members.length === 0 && <div style={NOTE}>Nobody yet.</div>}
          {members.map((member) => {
            const account = byKey.get(member.userKey);
            const label = account?.email ?? account?.displayName ?? member.userKey;
            return (
              <div key={member.userKey} style={ROW}>
                <span style={{ flex: "1 1 auto" }}>
                  {label}
                  {label !== member.userKey && <span style={NOTE}> {member.userKey}</span>}
                  {!account && directory.length < DIRECTORY_LIMIT && (
                    <span style={NOTE}> · no account yet</span>
                  )}
                </span>
                <span style={NOTE}>{member.role}</span>
                <button
                  type="button"
                  style={buttonStyle()}
                  onClick={() =>
                    void removeMember(org.key, member.userKey)
                      .then(() => fetchMembers(org.key).then(setMembers))
                      .then(reload) // the member count on the group row
                      .catch((error: unknown) =>
                        onError(error instanceof Error ? error.message : String(error)),
                      )
                  }
                >
                  Remove
                </button>
              </div>
            );
          })}
          <div style={ROW}>
            <input
              style={{ ...INPUT, flex: "1 1 auto" }}
              placeholder="email, name or user key"
              value={candidate}
              onChange={(event) => setCandidate(event.target.value)}
            />
            <button
              type="button"
              style={buttonStyle()}
              onClick={() => addByKey(org.key, candidate)}
            >
              Add
            </button>
          </div>
          {matches
            .filter((each) => !members.some((m) => m.userKey === each.key))
            .map((each) => (
              <div key={each.key} style={ROW}>
                <button
                  type="button"
                  style={{ ...buttonStyle(), flex: "1 1 auto", textAlign: "left" }}
                  onClick={() => addByKey(org.key, each.key)}
                >
                  {each.email ?? each.displayName ?? each.key}{" "}
                  <span style={NOTE}>
                    {each.key}
                    {each.orgs.length > 0 && ` · ${each.orgs.join(", ")}`}
                  </span>
                </button>
              </div>
            ))}
          <div style={NOTE}>
            Type an email or a name to find an account, then click it — the
            membership is stored against that account's <em>key</em>, which is
            the value that survives an email change. A key typed straight in is
            added as it stands, because the account need not exist yet: a club is
            usually set up before its members sign in.
          </div>

          <div style={SECTION}>{org.name} — what it grants</div>
          <div style={NOTE}>
            Grants add to whatever the member's other groups already gave them —
            never subtract. Under <strong>Markets and universes</strong>, the
            <em> every universe</em> rows are a whole market; the rest are one
            universe each.
          </div>
          <GrantEditor
            kind="org"
            subjectKey={org.key}
            vocabulary={vocabulary}
            onError={onError}
          />

          <div style={ROW}>
            <button
              type="button"
              style={buttonStyle()}
              onClick={() =>
                void patchOrg(org.key, { disabled: !org.disabled })
                  .then(reload)
                  .catch((error: unknown) =>
                    onError(error instanceof Error ? error.message : String(error)),
                  )
              }
            >
              {org.disabled ? "Enable" : "Disable"} this group
            </button>
            <span style={NOTE}>
              Disabling keeps its members and grants and stops both counting.
            </span>
          </div>
        </>
      )}
    </div>
  );
}

function Plans({
  vocabulary,
  onError,
}: {
  vocabulary: Vocabulary;
  onError: (message: string | null) => void;
}) {
  const [plans, setPlans] = useState<PlanGrants[] | null>(null);
  const [open, setOpen] = useState<string | null>(null);

  useEffect(() => {
    void fetchPlans().then(setPlans);
  }, []);

  const legacy = (
    <div style={NOTE}>
      <strong>Legacy.</strong> Plans predate product groups and do the same job
      worse — <code>app_user.tier</code> holds one string, so it could never
      express "US <em>and</em> India". Grant on a product group instead. This tab
      stays until every account has drained into one, which happens on their next
      sign-in.
    </div>
  );

  if (plans === null) return <div style={NOTE}>Loading…</div>;
  if (plans.length === 0) {
    return (
      <div>
        {legacy}
        <div style={NOTE}>No plan holds a grant. Nothing here is in force.</div>
      </div>
    );
  }

  return (
    <div>
      {legacy}
      {plans.map((plan) => (
        <div key={plan.plan}>
          <div style={ROW}>
            <button
              type="button"
              style={{ ...buttonStyle(), flex: "1 1 auto", textAlign: "left" }}
              onClick={() => setOpen(open === plan.plan ? null : plan.plan)}
            >
              <strong>{plan.plan}</strong>{" "}
              <span style={NOTE}>
                {plan.grants.some((g) => g.capability === "*")
                  ? "everything"
                  : `${plan.grants.length} grants`}
              </span>
            </button>
          </div>
          {open === plan.plan && (
            <GrantEditor
              kind="plan"
              subjectKey={plan.plan}
              vocabulary={vocabulary}
              onError={onError}
            />
          )}
        </div>
      ))}
    </div>
  );
}

function Accounts({
  vocabulary,
  onError,
}: {
  vocabulary: Vocabulary;
  onError: (message: string | null) => void;
}) {
  const [query, setQuery] = useState("");
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [effective, setEffective] = useState<Effective | null>(null);

  const search = useCallback(() => {
    void fetchUsers(query).then(setUsers);
  }, [query]);
  useEffect(search, [search]);

  useEffect(() => {
    if (!selected) {
      setEffective(null);
      return;
    }
    void fetchEffective(selected).then(setEffective);
  }, [selected]);

  return (
    <div>
      <div style={ROW}>
        <input
          style={{ ...INPUT, flex: "1 1 auto" }}
          placeholder="search by key or email"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
      </div>
      {users.map((user) => (
        <div key={user.key} style={ROW}>
          <button
            type="button"
            style={{ ...buttonStyle(), flex: "1 1 auto", textAlign: "left" }}
            onClick={() => setSelected(user.key === selected ? null : user.key)}
          >
            {user.email ?? user.key}{" "}
            <span style={NOTE}>
              {user.plan}
              {user.orgs.length > 0 && ` · ${user.orgs.join(", ")}`}
              {user.isTest && " · test"}
            </span>
          </button>
          <input
            style={{ ...INPUT, width: 90 }}
            defaultValue={user.plan}
            onBlur={(event) => {
              const plan = event.target.value.trim();
              if (!plan || plan === user.plan) return;
              void setUserPlan(user.key, plan)
                .then(search)
                .catch((error: unknown) =>
                  onError(error instanceof Error ? error.message : String(error)),
                );
            }}
          />
        </div>
      ))}

      {effective && selected && (
        <>
          <div style={SECTION}>{selected} — effective</div>
          {effective.permissive ? (
            <div style={NOTE}>
              Permissive: no grant has been written, so everything is allowed.
            </div>
          ) : (
            <div style={NOTE}>
              {effective.capabilities.length === 0
                ? "Holds nothing."
                : effective.capabilities.join("  ·  ")}
            </div>
          )}

          <div style={SECTION}>where it comes from</div>
          {effective.sources.map((source, index) => (
            <div key={`${source.kind}:${source.key}:${index}`} style={NOTE}>
              <strong>
                {source.kind} {source.key}
              </strong>
              {source.via && ` (via club ${source.via})`} —{" "}
              {source.grants.length === 0
                ? "nothing"
                : source.grants
                    .map(
                      (g) =>
                        `${g.capability}${g.limit === null ? "" : ` = ${g.limit}`}${
                          g.effect === "deny" ? " DENY" : ""
                        }`,
                    )
                    .join(", ")}
            </div>
          ))}

          <div style={SECTION}>just for this account</div>
          <div style={NOTE}>
            Grants written here add to what their plan and clubs already gave.
          </div>
          <GrantEditor
            kind="user"
            subjectKey={selected}
            vocabulary={vocabulary}
            onError={onError}
          />
        </>
      )}
    </div>
  );
}

/**
 * Published screener presets — park one, or remove one published by mistake.
 *
 * **Authoring is not here.** A preset *is* a screen, and the place to build a
 * screen is the screener: an admin configures the columns and the filter there
 * and presses Publish, which takes what is on the screen. Re-creating that
 * editor inside this panel would be a second column picker to keep in step with
 * the first, and it would ask somebody to author a screen they cannot see.
 */
function Presets({ onError }: { onError: (message: string | null) => void }) {
  const [presets, setPresets] = useState<AdminPreset[] | null>(null);

  const reload = useCallback(() => {
    void fetchAdminPresets().then(setPresets);
  }, []);
  useEffect(reload, [reload]);

  const guard = (work: Promise<unknown>) =>
    void work.then(reload).catch((error: unknown) =>
      onError(error instanceof Error ? error.message : String(error)),
    );

  if (presets === null) return <div style={NOTE}>Loading…</div>;
  if (presets.length === 0) {
    return (
      <div style={NOTE}>
        No presets yet. Open the screener, set up the columns and filter you
        want, then use <strong>Presets → Publish this screen</strong> in the
        screener editor.
      </div>
    );
  }

  return (
    <div>
      {presets.map((preset) => (
        <div key={preset.key} style={ROW}>
          <span style={{ flex: "1 1 auto", opacity: preset.isActive ? 1 : 0.5 }}>
            {preset.name}{" "}
            <span style={NOTE}>
              {preset.ownerKind === "system" ? "built in" : preset.ownerKey}
              {preset.groupCodes.length > 0 && ` · ${preset.groupCodes.join(", ")}`}
              {!preset.isActive && " · parked"}
            </span>
          </span>
          <button
            type="button"
            style={buttonStyle()}
            onClick={() => guard(setPresetActive(preset.key, !preset.isActive))}
          >
            {preset.isActive ? "Park" : "Un-park"}
          </button>
          <button
            type="button"
            style={buttonStyle()}
            onClick={() => guard(deletePreset(preset.key))}
          >
            Delete
          </button>
        </div>
      ))}
      <div style={NOTE}>
        Parking hides a preset from the pickers and keeps it recoverable.
        Deleting does not remove the grant — a key can be republished, and
        cascading would silently drop entitlements a club would have to re-issue.
      </div>

      <PublishedLayouts onError={onError} />
      <PublishedChartTemplates onError={onError} />
    </div>
  );
}

/**
 * Published layouts — park one, or remove one published by mistake.
 *
 * **Under the presets rather than in a fifth tab.** The tab strip already
 * carries four, and both halves of this one answer the same question — what has
 * this club published, and is it still on offer. A tab of its own would also
 * put two nearly identical screens a click apart, which is how they drift.
 *
 * **Authoring is not here**, for the preset's reason twice over: a layout *is*
 * an arrangement, and the place to build one is the layout panel, where you can
 * see it. Re-creating that here would be a second arrangement editor to keep in
 * step with the first, in a panel that covers the charts it would be editing.
 */
function PublishedLayouts({ onError }: { onError: (message: string | null) => void }) {
  const [layouts, setLayouts] = useState<AdminLayout[] | null>(null);

  const reload = useCallback(() => {
    void fetchAdminLayouts().then(setLayouts);
  }, []);
  useEffect(reload, [reload]);

  const guard = (work: Promise<unknown>) =>
    void work.then(reload).catch((error: unknown) =>
      onError(error instanceof Error ? error.message : String(error)),
    );

  if (layouts === null) return null;

  return (
    <>
      <div style={SECTION}>Layouts</div>
      {layouts.length === 0 ? (
        <div style={NOTE}>
          No layouts yet. Arrange the charts you want, then use the pencil circle
          in the top row — <strong>Publish…</strong> beside New layout.
        </div>
      ) : (
        layouts.map((layout) => (
          <div key={layout.key} style={ROW}>
            <span style={{ flex: "1 1 auto", opacity: layout.isActive ? 1 : 0.5 }}>
              {layout.name}{" "}
              <span style={NOTE}>
                {layout.ownerKind === "system" ? "built in" : layout.ownerKey}
                {` · ${layout.chartCount} charts`}
                {!layout.isActive && " · parked"}
              </span>
            </span>
            <button
              type="button"
              style={buttonStyle()}
              onClick={() => guard(setLayoutActive(layout.key, !layout.isActive))}
            >
              {layout.isActive ? "Park" : "Un-park"}
            </button>
            <button
              type="button"
              style={buttonStyle()}
              onClick={() => guard(deleteLayout(layout.key))}
            >
              Delete
            </button>
          </div>
        ))
      )}
    </>
  );
}

export function AdminPanel() {
  const [tab, setTab] = useState<Tab>("groups");
  const [vocabulary, setVocabulary] = useState<Vocabulary>(EMPTY_VOCABULARY);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void fetchVocabulary().then(setVocabulary);
  }, []);

  return (
    <div style={PANEL}>
      <div style={TABS}>
        <button
          type="button"
          style={tabStyle(tab === "groups")}
          onClick={() => setTab("groups")}
        >
          Groups
        </button>
        <button type="button" style={tabStyle(tab === "plans")} onClick={() => setTab("plans")}>
          Plans
        </button>
        <button
          type="button"
          style={tabStyle(tab === "accounts")}
          onClick={() => setTab("accounts")}
        >
          Accounts
        </button>
        {/* Beside Accounts: who exists there, who uses the app here
            (docs/plans/usage-tracking.md). */}
        <button type="button" style={tabStyle(tab === "usage")} onClick={() => setTab("usage")}>
          Usage
        </button>
        <button
          type="button"
          style={tabStyle(tab === "presets")}
          onClick={() => setTab("presets")}
        >
          Presets
        </button>
        {/* Its own tab rather than a section of Groups: a request is not a
            group yet, and the queue is read on its own — what is waiting. */}
        <button
          type="button"
          style={tabStyle(tab === "requests")}
          onClick={() => setTab("requests")}
        >
          Group requests
        </button>
        {/* Which provider writes the Summary (docs/features/summary.md). */}
        <button type="button" style={tabStyle(tab === "ai")} onClick={() => setTab("ai")}>
          AI
        </button>
        {/* What each user's Summaries cost (docs/features/summary.md). */}
        <button type="button" style={tabStyle(tab === "ledger")} onClick={() => setTab("ledger")}>
          Ledger
        </button>
      </div>

      {error && <div style={ERROR}>{plainText(error)}</div>}

      {tab === "groups" && <Groups vocabulary={vocabulary} onError={setError} />}
      {tab === "plans" && <Plans vocabulary={vocabulary} onError={setError} />}
      {tab === "accounts" && <Accounts vocabulary={vocabulary} onError={setError} />}
      {tab === "usage" && <AdminUsage />}
      {tab === "presets" && <Presets onError={setError} />}
      {tab === "requests" && <GroupRequestsAdmin />}
      {tab === "ai" && <AdminAi />}
      {tab === "ledger" && <AdminLedger />}

      <div style={NOTE}>
        Roles are deliberately not here. Granting <code>admin</code> is granting
        the ability to act as other accounts, which stays a console command —
        see <code>app/user/cli.py</code>.
      </div>
    </div>
  );
}

/**
 * Published chart templates — park one, or remove one published by mistake.
 *
 * `PublishedLayouts`' twin, under it, and for its reasons: both answer the same
 * question — what has this club published, and is it still on offer — and a tab
 * of its own would put three nearly identical screens a click apart, which is
 * how they drift.
 *
 * **A user's own templates are listed too**, unlike the two sections above,
 * which only ever hold `system` and `org` rows. `/admin/chart-templates` reads
 * the whole table and the personal case is the common one here, so hiding them
 * would make the count on this screen disagree with the table for no reason an
 * admin could see. They are labelled by owner like everything else.
 */
function PublishedChartTemplates({ onError }: { onError: (message: string | null) => void }) {
  const [templates, setTemplates] = useState<AdminChartTemplate[] | null>(null);

  const reload = useCallback(() => {
    void fetchAdminChartTemplates().then(setTemplates);
  }, []);
  useEffect(reload, [reload]);

  const guard = (work: Promise<unknown>) =>
    void work.then(reload).catch((error: unknown) =>
      onError(error instanceof Error ? error.message : String(error)),
    );

  if (templates === null) return null;

  return (
    <>
      <div style={SECTION}>Chart templates</div>
      {templates.length === 0 ? (
        <div style={NOTE}>
          No chart templates yet. Arrange a chart, then right-click it —{" "}
          <strong>Save chart as template…</strong>, with <em>Share</em> ticked.
        </div>
      ) : (
        templates.map((template) => (
          <div key={template.key} style={ROW}>
            <span style={{ flex: "1 1 auto", opacity: template.isActive ? 1 : 0.5 }}>
              {template.name}{" "}
              <span style={NOTE}>
                {template.ownerKind === "system" ? "built in" : template.ownerKey}
                {` · ${template.paneCount} pane${template.paneCount === 1 ? "" : "s"}`}
                {template.symbol ? ` · ${template.symbol}` : ""}
                {!template.isActive && " · parked"}
              </span>
            </span>
            <button
              type="button"
              style={buttonStyle()}
              onClick={() => guard(setChartTemplateActive(template.key, !template.isActive))}
            >
              {template.isActive ? "Park" : "Un-park"}
            </button>
            <button
              type="button"
              style={buttonStyle()}
              onClick={() => guard(deleteChartTemplate(template.key))}
            >
              Delete
            </button>
          </div>
        ))
      )}
    </>
  );
}
